<script setup lang="ts">
import type { ShopperProductsTypes, ShopperPromotionsTypes } from 'commerce-sdk-isomorphic'

type PromotionWithDiscountedProducts = ShopperPromotionsTypes.Promotion & {
  c_discountedProducts?: string[]
  discountedProductsWithData?: FlattenedProduct[]
}

type Props = {
  inMyBag?: boolean
  promotions: ShopperProductsTypes.ProductPromotion[] | undefined
}

const props = defineProps<Props>()
const emit = defineEmits(['promo-added'])
const { getPromotionsByIds, result: promotionsResult } = usePromotions()

const basketsStore = useBasketsStore()
const { addToBagAction } = useCartActions()
const freeProductPromotions = ref<PromotionWithDiscountedProducts[]>([])

const getFreeProductPromotions = async (promotions: ShopperProductsTypes.ProductPromotion[] | undefined) => {
  if (!promotions) {
    return
  }

  const ids = promotions?.map((promotion) => promotion.promotionId)
  const promotionIds = [...new Set(ids)]
  if (promotionIds.length === 0) {
    return
  }

  const promoDetailsError = await getPromotionsByIds(promotionIds)
  if (promoDetailsError) {
    console.error('Error fetching promotion details', promoDetailsError)
    return
  }
  const freeProductPromotions = promotionsResult.value?.filter(
    (promotion): promotion is PromotionWithDiscountedProducts =>
      Array.isArray(promotion.c_discountedProducts) && promotion.c_discountedProducts.length > 0,
  )

  if (!freeProductPromotions) {
    return
  }

  const promotionsWithFreeProductData: PromotionWithDiscountedProducts[] = []
  for (const promotion of freeProductPromotions) {
    const uniqueProductIds = [...new Set(promotion.c_discountedProducts)]
    const isTheDiscountedProduct = uniqueProductIds.some((id) => {
      return basketsStore.isItemInBasket(id)
    })
    // the promotion displays promotion callout on the discounted products defined in salesforce, need to ensure if adding a product that is in the dsicounted list we do not show the bonus offer as the promotion would not be valid
    if (isTheDiscountedProduct) {
      continue
    }

    const products = await useProducts().getProducts(uniqueProductIds)
    promotionsWithFreeProductData.push({
      ...promotion,
      discountedProductsWithData: products instanceof Error ? [] : products,
    })
  }

  if (!promotionsWithFreeProductData.length) {
    return
  }

  return promotionsWithFreeProductData
}

watch(
  () => props.promotions,
  async (newPromotions) => {
    const freeProductPromos = await getFreeProductPromotions(newPromotions)
    if (freeProductPromos) {
      freeProductPromotions.value = freeProductPromos
    }
  },
  { immediate: true },
)

function isShowBonusOffer(product: FlattenedProduct) {
  return (
    product.id &&
    !basketsStore.isItemInBasket(product.id) &&
    product?.inventory?.orderable &&
    (product?.inventory?.ats || 0) > 0
  )
}

function bonusProductImage(product: FlattenedProduct) {
  return {
    url: findPrimaryAngle(product?.images || []),
    alt: product?.name || '',
  }
}

const addBonusToBag = async (product: FlattenedProduct) => {
  // Adding from inside My Bag stays in place; elsewhere the added bonus opens AddToBagPopup.
  const popupParams = props.inMyBag
    ? {}
    : {
        popup: {
          selectedPcpOption: null,
          product: product,
          isGiftWithPurchase: true, // Added bonus product now sits alongside in AddToBagPopup
        },
      }

  await addToBagAction(
    {
      productId: product.id,
      ...popupParams,
    },
    {
      group: 'My Bag',
      product: product,
      location: 'my bag upsell popup',
    },
  )

  if (!basketsStore.isItemInBasket(product.id)) {
    return
  }

  const basketItem = basketsStore.getItem(product.id)

  if (!basketItem) {
    return
  }

  const cardSummaryProduct = {
    productId: basketItem.productId,
    productName: basketItem.productName,
    price: basketItem.priceAfterItemDiscount,
    image: basketItem.image,
    url: product.slugUrl,
    promotions: product.productPromotions,
  }
  emit('promo-added', cardSummaryProduct)
}
</script>

<template>
  <TransitionGroup name="fade-out">
    <div v-for="(promo, index) in freeProductPromotions" :key="promo.id ?? index">
      <!-- 24/02/2026 - MHJ-247115 Currently built with restriction of 1 product available for selection as per instruction from trade,
       UX will be slightly different for multiple products but should be easy enough to use existing logic to 
       support multiple selections in the future when that ticket rolls around -->
      <h2 v-if="!inMyBag && isShowBonusOffer(promo?.discountedProductsWithData?.[0])">
        {{ $t('product_details_page.add_to_bag_popup.bonus_offer_title') }}
      </h2>
      <section v-if="isShowBonusOffer(promo?.discountedProductsWithData?.[0])" class="bonus-product-promotion">
        <div class="bonus-product-promotion__image">
          <ProductCardImage
            :image="bonusProductImage(promo?.discountedProductsWithData?.[0])"
            :product-url="undefined"
          />
        </div>
        <div class="bonus-product-promotion__content">
          <div class="bonus-product-promotion__callout">
            <p v-if="inMyBag">
              {{ $t('product_details_page.add_to_bag_popup.bonus_offer_title') }}
            </p>
            <p>{{ promo?.discountedProductsWithData?.[0]?.name }}</p>
          </div>
          <Button
            variant="tertiary"
            @click="addBonusToBag(promo?.discountedProductsWithData?.[0])"
            :disabled="basketsStore.loading"
          >
            {{ $t('product_details_page.add_to_bag.button.text') }}
          </Button>
        </div>
      </section>
    </div>
  </TransitionGroup>
</template>

<style lang="postcss">
.bonus-product-promotion {
  background-color: var(--surface-light-40);
  padding: var(--space-50);
  display: flex;
  gap: var(--space-60);

  .bonus-product-promotion__image {
    flex: 0 0 6rem;
  }

  .bonus-product-promotion__content {
    display: flex;
    flex-direction: column;
    gap: var(--space-50);
  }

  .bonus-product-promotion__callout {
    p {
      margin-bottom: 0;
    }
  }

  button {
    width: max-content;
  }
}

.fade-out-leave-active {
  transition: opacity 0.3s ease-out;
}

.fade-out-leave-to {
  opacity: 0;
}
</style>
